/**
 * Losi Pricing
 *
 * Central monetization source of truth for Losi personal plans, Losi
 * Business bundles, legacy plan normalization, and wallet defaults.
 *
 * Primary responsibilities:
 * - Define public personal and business pricing
 * - Map business bundles to internal Nexus plans
 * - Normalize legacy personal and Nexus plan ids
 * - Expose shared wallet, add-on, and service pricing
 *
 * Dependencies:
 * - Canada Day Ultra Week campaign config
 */

import { isCanadaDayUltraWeekActive } from '@/lib/campaigns/canada-day-ultra-week'

export type BillingCycle = 'monthly' | 'yearly'

export type LosiPersonalPlanId = 'free' | 'plus' | 'pro' | 'ultra'

export type LosiBusinessPlanId =
  | 'business_starter'
  | 'business_pro'
  | 'business_ultra'

export type NexusInternalPlanId = 'starter' | 'professional' | 'enterprise'

export type LosiPublicPlanId =
  | LosiPersonalPlanId
  | LosiBusinessPlanId
  | 'enterprise'

export type LosiSpaceObjectType =
  | 'chat'
  | 'note'
  | 'folder'
  | 'task'
  | 'event'
  | 'sprint'
  | 'sheet'
  | 'whiteboard'
  | 'design'

export type LosiSpaceObjectAvailability =
  | 'create'
  | 'create_when_supported'
  | 'no'

type PersonalStripeLookupKey =
  | 'plus_monthly'
  | 'plus_yearly'
  | 'pro_monthly'
  | 'pro_yearly'
  | 'ultra_monthly'
  | 'ultra_yearly'

type LegacyPersonalStripeLookupKey =
  | 'premium_monthly'
  | 'premium_yearly'
  | 'studio_subscription'
  | 'byok_monthly'
  | 'byok_yearly'

type BusinessStripeLookupKey =
  | 'nexus_starter_monthly'
  | 'nexus_starter_yearly'
  | 'nexus_professional_monthly'
  | 'nexus_professional_yearly'
  | 'nexus_enterprise_monthly'
  | 'nexus_enterprise_yearly'

export type RecognizedPersonalStripeLookupKey =
  | PersonalStripeLookupKey
  | LegacyPersonalStripeLookupKey

export type RecognizedBusinessStripeLookupKey = BusinessStripeLookupKey

export interface LosiPersonalPlanConfig {
  id: LosiPersonalPlanId
  publicName: string
  monthlyPriceUsd: number
  yearlyPriceUsd?: number
  yearlyMonthlyPriceUsd?: number
  yearlySavingsPercent?: number
  description: string
  positioning: string
  featured?: boolean
  stripeLookupKeys: Partial<Record<BillingCycle, PersonalStripeLookupKey>>
  featureBullets: readonly string[]
}

export interface LosiBusinessPlanConfig {
  id: LosiBusinessPlanId
  publicName: string
  monthlyPriceUsd: number
  yearlyPriceUsd?: number
  yearlyMonthlyPriceUsd?: number
  yearlySavingsPercent?: number
  description: string
  positioning: string
  nexusPlan: NexusInternalPlanId
  includedCredits: number
  overageRateUsd: number
  includedProSeats: number
  includedAgents: number
  stripeLookupKeys: Partial<Record<BillingCycle, BusinessStripeLookupKey>>
  featureBullets: readonly string[]
}

export interface LosiAddOnConfig {
  id: string
  publicName: string
  priceLabel: string
  description: string
}

export interface LosiAttachmentLimitConfig {
  maxAttachmentSizeBytes: number | null
  maxAttachmentSizeLabel: string
  filesPerUpload: number | null
  filesPerUploadLabel: string
}

export interface LosiExecutionLimitConfig {
  activeTaskLimit: number | null
  monthlyPlanToTaskConversionLimit: number | null
  activeRecurringTaskLimit: number | null
  upcomingEventLimit: number | null
  activeSprintLimit: number | null
}

export interface LosiBillingPriceSnapshot {
  billingCycle: BillingCycle
  monthlyEquivalentUsd: number
  billedUpfrontUsd: number
  savingsPercent: number
}

export interface LosiServiceConfig {
  id: string
  publicName: string
  priceLabel: string
  description: string
}

const PERSONAL_PLAN_ORDER = ['free', 'plus', 'pro', 'ultra'] as const

const BUSINESS_PLAN_ORDER = [
  'business_starter',
  'business_pro',
  'business_ultra'
] as const

const PERSONAL_PLAN_RANK: Record<LosiPersonalPlanId, number> = {
  free: 0,
  plus: 1,
  pro: 2,
  ultra: 3
}

const megabytes = (value: number) => value * 1024 * 1024

const gigabytes = (value: number) => value * 1024 * 1024 * 1024

export const LOSI_PUBLIC_PERSONAL_BILLING_CYCLES = [
  'monthly',
  'yearly'
] as const

export const LOSI_PUBLIC_BUSINESS_BILLING_CYCLES = [
  'monthly',
  'yearly'
] as const

export const LOSI_PERSONAL_PLANS: Record<
  LosiPersonalPlanId,
  LosiPersonalPlanConfig
> = {
  free: {
    id: 'free',
    publicName: 'Free',
    monthlyPriceUsd: 0,
    description: 'Start small and see Losi turn plans into progress.',
    positioning: 'Chat, notes, Spaces, and light execution, free.',
    stripeLookupKeys: {},
    featureBullets: [
      'Chats, notes, folders, Spaces, basic tasks, and light events',
      'Enough tasks, conversions, and follow-ups to feel the loop',
      'A light monthly pool for nudges and automation actions',
      'Small attachments and simple upload batches'
    ]
  },
  plus: {
    id: 'plus',
    publicName: 'Plus',
    monthlyPriceUsd: 9.99,
    yearlyPriceUsd: 59.88,
    yearlyMonthlyPriceUsd: 4.99,
    yearlySavingsPercent: 50,
    description: 'More room when Losi becomes part of the work.',
    positioning: 'For growing plans that need more tasks, follow-ups, events, and sprints.',
    stripeLookupKeys: {
      monthly: 'plus_monthly',
      yearly: 'plus_yearly'
    },
    featureBullets: [
      'More room for active tasks, follow-ups, events, and plan conversions',
      'Sprints for focused project cycles',
      'More nudge and automation actions as projects grow',
      'Bigger uploads, more daily throughput, and better all-purpose models'
    ]
  },
  pro: {
    id: 'pro',
    publicName: 'Pro',
    monthlyPriceUsd: 19.99,
    yearlyPriceUsd: 119.88,
    yearlyMonthlyPriceUsd: 9.99,
    yearlySavingsPercent: 50,
    description: 'Add structure when the work needs tables, boards, and visuals.',
    positioning: 'Sheets, Whiteboard, Design, larger files, and advanced workspace tools.',
    featured: true,
    stripeLookupKeys: {
      monthly: 'pro_monthly',
      yearly: 'pro_yearly'
    },
    featureBullets: [
      'Create sheets, whiteboards, and supported design work in Spaces',
      'More room for larger projects and structured planning',
      'More nudge, automation, and workflow action capacity',
      'Larger uploads for structured workspace files',
      'Premium tools, stronger models, and deeper work intelligence'
    ]
  },
  ultra: {
    id: 'ultra',
    publicName: 'Ultra',
    monthlyPriceUsd: 39.99,
    yearlyPriceUsd: 359.88,
    yearlyMonthlyPriceUsd: 29.99,
    yearlySavingsPercent: 25,
    description: 'Work without hitting the ceiling.',
    positioning: 'Big files, deep context, priority speed, and workspace-wide power.',
    stripeLookupKeys: {
      monthly: 'ultra_monthly',
      yearly: 'ultra_yearly'
    },
    featureBullets: [
      'Everything in Pro with the highest personal throughput and fastest access',
      'The most room for heavy multi-project work',
      'The largest personal pool for nudges, automations, and workflow actions',
      'Upload up to 10x larger files than Pro'
    ]
  }
}

export const LOSI_BUSINESS_PLANS: Record<
  LosiBusinessPlanId,
  LosiBusinessPlanConfig
> = {
  business_starter: {
    id: 'business_starter',
    publicName: 'Business Starter',
    monthlyPriceUsd: 99,
    yearlyPriceUsd: 1010,
    yearlyMonthlyPriceUsd: 84,
    yearlySavingsPercent: 15,
    description: 'Losi + Nexus bundled for small teams starting operations.',
    positioning:
      'Shared Spaces, Nexus operations, and one Pro seat to start.',
    nexusPlan: 'starter',
    includedCredits: 500,
    overageRateUsd: 0.2,
    includedProSeats: 1,
    includedAgents: 1,
    stripeLookupKeys: {
      monthly: 'nexus_starter_monthly',
      yearly: 'nexus_starter_yearly'
    },
    featureBullets: [
      'Includes Nexus Starter',
      'Includes a Losi Pro seat',
      'Entry Nexus credits with wallet overages',
      'Business upload capacity for shared work'
    ]
  },
  business_pro: {
    id: 'business_pro',
    publicName: 'Business Pro',
    monthlyPriceUsd: 249,
    yearlyPriceUsd: 2540,
    yearlyMonthlyPriceUsd: 212,
    yearlySavingsPercent: 15,
    description: 'Losi + Nexus for growing teams running more automation.',
    positioning:
      'More agents, deeper workflows, and stronger team execution.',
    nexusPlan: 'professional',
    includedCredits: 2000,
    overageRateUsd: 0.15,
    includedProSeats: 3,
    includedAgents: 5,
    stripeLookupKeys: {
      monthly: 'nexus_professional_monthly',
      yearly: 'nexus_professional_yearly'
    },
    featureBullets: [
      'Includes Nexus Professional',
      'Includes more Losi Pro seats',
      'More Nexus credits with better overage economics',
      'More business upload capacity for growing teams'
    ]
  },
  business_ultra: {
    id: 'business_ultra',
    publicName: 'Business Ultra',
    monthlyPriceUsd: 499,
    yearlyPriceUsd: 5090,
    yearlyMonthlyPriceUsd: 424,
    yearlySavingsPercent: 15,
    description: 'Losi + Nexus for high-volume teams and advanced operations.',
    positioning:
      'The full standard operations system for agents, credits, and support.',
    nexusPlan: 'enterprise',
    includedCredits: 10000,
    overageRateUsd: 0.1,
    includedProSeats: 5,
    includedAgents: 10,
    stripeLookupKeys: {
      monthly: 'nexus_enterprise_monthly',
      yearly: 'nexus_enterprise_yearly'
    },
    featureBullets: [
      'Includes Nexus Enterprise',
      'Includes the largest standard Pro seat bundle',
      'The strongest standard Nexus credit and overage economics',
      'The largest standard business upload capacity'
    ]
  }
}

export const LOSI_ENTERPRISE_PLAN = {
  id: 'enterprise' as const,
  publicName: 'Enterprise',
  priceLabel: 'Custom',
  description:
    'For advanced security, compliance, procurement, custom contracts, and larger organizations.',
  positioning: 'Contact sales for tailored rollout, governance, and support.'
}

export const LOSI_WALLET_CONFIG = {
  minimumTopUpUsd: 5,
  minimumTopUpCents: 500,
  defaultAutoLoadThresholdCents: 500,
  defaultAutoLoadAmountCents: 500
} as const

export const LOSI_EXECUTION_LIMITS: Record<
  LosiPublicPlanId,
  LosiExecutionLimitConfig
> = {
  free: {
    activeTaskLimit: 10,
    monthlyPlanToTaskConversionLimit: 3,
    activeRecurringTaskLimit: 1,
    upcomingEventLimit: 3,
    activeSprintLimit: 0
  },
  plus: {
    activeTaskLimit: 100,
    monthlyPlanToTaskConversionLimit: 25,
    activeRecurringTaskLimit: 10,
    upcomingEventLimit: 25,
    activeSprintLimit: 10
  },
  pro: {
    activeTaskLimit: 500,
    monthlyPlanToTaskConversionLimit: 100,
    activeRecurringTaskLimit: 50,
    upcomingEventLimit: 100,
    activeSprintLimit: 50
  },
  ultra: {
    activeTaskLimit: 2000,
    monthlyPlanToTaskConversionLimit: 500,
    activeRecurringTaskLimit: 200,
    upcomingEventLimit: 500,
    activeSprintLimit: 200
  },
  business_starter: {
    activeTaskLimit: 1000,
    monthlyPlanToTaskConversionLimit: 250,
    activeRecurringTaskLimit: 100,
    upcomingEventLimit: 250,
    activeSprintLimit: 100
  },
  business_pro: {
    activeTaskLimit: 5000,
    monthlyPlanToTaskConversionLimit: 1000,
    activeRecurringTaskLimit: 500,
    upcomingEventLimit: 1000,
    activeSprintLimit: 500
  },
  business_ultra: {
    activeTaskLimit: null,
    monthlyPlanToTaskConversionLimit: null,
    activeRecurringTaskLimit: null,
    upcomingEventLimit: null,
    activeSprintLimit: null
  },
  enterprise: {
    activeTaskLimit: null,
    monthlyPlanToTaskConversionLimit: null,
    activeRecurringTaskLimit: null,
    upcomingEventLimit: null,
    activeSprintLimit: null
  }
}

export const LOSI_SPACE_OBJECT_GATES: Record<
  LosiPublicPlanId,
  Record<LosiSpaceObjectType, LosiSpaceObjectAvailability>
> = {
  free: {
    chat: 'create',
    note: 'create',
    folder: 'create',
    task: 'create',
    event: 'create',
    sprint: 'no',
    sheet: 'no',
    whiteboard: 'no',
    design: 'no'
  },
  plus: {
    chat: 'create',
    note: 'create',
    folder: 'create',
    task: 'create',
    event: 'create',
    sprint: 'create_when_supported',
    sheet: 'no',
    whiteboard: 'no',
    design: 'no'
  },
  pro: {
    chat: 'create',
    note: 'create',
    folder: 'create',
    task: 'create',
    event: 'create',
    sprint: 'create',
    sheet: 'create',
    whiteboard: 'create',
    design: 'create_when_supported'
  },
  ultra: {
    chat: 'create',
    note: 'create',
    folder: 'create',
    task: 'create',
    event: 'create',
    sprint: 'create',
    sheet: 'create',
    whiteboard: 'create',
    design: 'create'
  },
  business_starter: {
    chat: 'create',
    note: 'create',
    folder: 'create',
    task: 'create',
    event: 'create',
    sprint: 'create',
    sheet: 'create',
    whiteboard: 'create',
    design: 'create'
  },
  business_pro: {
    chat: 'create',
    note: 'create',
    folder: 'create',
    task: 'create',
    event: 'create',
    sprint: 'create',
    sheet: 'create',
    whiteboard: 'create',
    design: 'create'
  },
  business_ultra: {
    chat: 'create',
    note: 'create',
    folder: 'create',
    task: 'create',
    event: 'create',
    sprint: 'create',
    sheet: 'create',
    whiteboard: 'create',
    design: 'create'
  },
  enterprise: {
    chat: 'create',
    note: 'create',
    folder: 'create',
    task: 'create',
    event: 'create',
    sprint: 'create',
    sheet: 'create',
    whiteboard: 'create',
    design: 'create'
  }
}

export const LOSI_ATTACHMENT_LIMITS: Record<
  LosiPublicPlanId,
  LosiAttachmentLimitConfig
> = {
  free: {
    maxAttachmentSizeBytes: megabytes(1),
    maxAttachmentSizeLabel: '1 MB',
    filesPerUpload: 3,
    filesPerUploadLabel: '3'
  },
  plus: {
    maxAttachmentSizeBytes: megabytes(5),
    maxAttachmentSizeLabel: '5 MB',
    filesPerUpload: 10,
    filesPerUploadLabel: '10'
  },
  pro: {
    maxAttachmentSizeBytes: megabytes(50),
    maxAttachmentSizeLabel: '50 MB',
    filesPerUpload: 25,
    filesPerUploadLabel: '25'
  },
  ultra: {
    maxAttachmentSizeBytes: megabytes(500),
    maxAttachmentSizeLabel: '500 MB',
    filesPerUpload: 50,
    filesPerUploadLabel: '50'
  },
  business_starter: {
    maxAttachmentSizeBytes: gigabytes(5),
    maxAttachmentSizeLabel: '5 GB',
    filesPerUpload: 100,
    filesPerUploadLabel: '100'
  },
  business_pro: {
    maxAttachmentSizeBytes: gigabytes(10),
    maxAttachmentSizeLabel: '10 GB',
    filesPerUpload: 250,
    filesPerUploadLabel: '250'
  },
  business_ultra: {
    maxAttachmentSizeBytes: gigabytes(25),
    maxAttachmentSizeLabel: '25 GB',
    filesPerUpload: 500,
    filesPerUploadLabel: '500'
  },
  enterprise: {
    maxAttachmentSizeBytes: null,
    maxAttachmentSizeLabel: 'Custom',
    filesPerUpload: null,
    filesPerUploadLabel: 'Custom'
  }
}

export const NEXUS_CREDIT_RULES = {
  voiceCallPerMinute: 5,
  chatMessage: 1,
  workflowRun: 1,
  workflowStep: 0.5
} as const

export const LOSI_ADD_ONS: readonly LosiAddOnConfig[] = [
  {
    id: 'hipaa',
    publicName: 'HIPAA Add-On',
    priceLabel: 'Starts at $199/mo',
    description: 'Compliance support for regulated healthcare workflows.'
  },
  {
    id: 'automation_pack',
    publicName: 'Automation Pack',
    priceLabel: '$99/mo',
    description: 'Expanded automation capacity and packaged workflow support.'
  },
  {
    id: 'agent_pack',
    publicName: 'Agent Pack',
    priceLabel: '$49/mo',
    description: 'Additional packaged agent capacity for operational teams.'
  },
  {
    id: 'priority_support',
    publicName: 'Priority Support',
    priceLabel: '$99/mo',
    description: 'Faster response handling for business-critical operations.'
  },
  {
    id: 'success_manager',
    publicName: 'Dedicated Success Manager',
    priceLabel: '$299/mo',
    description: 'Named strategic and operational support contact.'
  },
  {
    id: 'emergency_support',
    publicName: '24/7 Emergency Support',
    priceLabel: '$499/mo',
    description: 'Around-the-clock emergency assistance for urgent issues.'
  }
] as const

export const LOSI_SERVICES: readonly LosiServiceConfig[] = [
  {
    id: 'basic_setup',
    publicName: 'Basic Setup',
    priceLabel: '$499 one-time',
    description: 'Lightweight onboarding and activation support.'
  },
  {
    id: 'standard_implementation',
    publicName: 'Standard Implementation',
    priceLabel: '$2,499 one-time',
    description: 'Structured implementation for business rollout.'
  },
  {
    id: 'enterprise_implementation',
    publicName: 'Enterprise Implementation',
    priceLabel: '$7,499 to $24,999',
    description: 'White-glove rollout for complex teams and integrations.'
  },
  {
    id: 'training_and_enablement',
    publicName: 'Training and Enablement',
    priceLabel: 'Custom',
    description: 'Training, enablement, and adoption support.'
  },
  {
    id: 'integration_services',
    publicName: 'Integration Services',
    priceLabel: 'Custom',
    description: 'Custom integration, migration, and workflow support.'
  }
] as const

export const ACTIVE_PERSONAL_STRIPE_LOOKUP_KEYS = Object.values(
  LOSI_PERSONAL_PLANS
)
  .flatMap((plan) => Object.values(plan.stripeLookupKeys))
  .filter((lookupKey): lookupKey is PersonalStripeLookupKey => Boolean(lookupKey))

export const ACTIVE_BUSINESS_STRIPE_LOOKUP_KEYS = Object.values(
  LOSI_BUSINESS_PLANS
)
  .flatMap((plan) => Object.values(plan.stripeLookupKeys))
  .filter((lookupKey): lookupKey is BusinessStripeLookupKey => Boolean(lookupKey))

export const LEGACY_PERSONAL_STRIPE_LOOKUP_KEYS: readonly LegacyPersonalStripeLookupKey[] =
  [
    'premium_monthly',
    'premium_yearly',
    'studio_subscription',
    'byok_monthly',
    'byok_yearly'
  ] as const

export const RECOGNIZED_PERSONAL_STRIPE_LOOKUP_KEYS: readonly RecognizedPersonalStripeLookupKey[] =
  [...ACTIVE_PERSONAL_STRIPE_LOOKUP_KEYS, ...LEGACY_PERSONAL_STRIPE_LOOKUP_KEYS]

export const RECOGNIZED_BUSINESS_STRIPE_LOOKUP_KEYS: readonly RecognizedBusinessStripeLookupKey[] =
  [...ACTIVE_BUSINESS_STRIPE_LOOKUP_KEYS]

export const isLegacyPersonalPlan = (plan: string | null | undefined) => {
  const normalizedPlan = plan?.toLowerCase() || ''
  return (
    normalizedPlan.startsWith('premium') ||
    normalizedPlan.startsWith('studio') ||
    normalizedPlan.startsWith('byok')
  )
}

export const isLegacyStudioPlan = (plan: string | null | undefined) => {
  const normalizedPlan = plan?.toLowerCase() || ''
  return normalizedPlan.startsWith('premium') || normalizedPlan.startsWith('studio')
}

export const isLegacyByokPlan = (plan: string | null | undefined) => {
  const normalizedPlan = plan?.toLowerCase() || ''
  return normalizedPlan.startsWith('byok')
}

export const normalizePersonalPlanTier = (
  plan: string | null | undefined
): LosiPersonalPlanId => {
  const normalizedPlan = plan?.toLowerCase() || ''

  if (normalizedPlan.startsWith('plus')) return 'plus'
  if (normalizedPlan.startsWith('pro')) return 'pro'
  if (normalizedPlan.startsWith('ultra')) return 'ultra'
  if (normalizedPlan.startsWith('premium')) return 'ultra'
  if (normalizedPlan.startsWith('studio')) return 'ultra'
  if (normalizedPlan.startsWith('byok')) return 'pro'

  return 'free'
}

export const getPersonalPlanRank = (plan: LosiPersonalPlanId) =>
  PERSONAL_PLAN_RANK[plan]

export const pickHigherPersonalPlan = (
  left: LosiPersonalPlanId,
  right: LosiPersonalPlanId
) => {
  return getPersonalPlanRank(left) >= getPersonalPlanRank(right) ? left : right
}

export const hasPaidPersonalPlan = (plan: string | null | undefined) => {
  return normalizePersonalPlanTier(plan) !== 'free'
}

export const getPersonalPlanFromLookupKey = (
  lookupKey: string | null | undefined
): LosiPersonalPlanId | null => {
  if (!lookupKey) return null

  const normalizedLookupKey = lookupKey.toLowerCase()

  for (const planId of PERSONAL_PLAN_ORDER) {
    const plan = LOSI_PERSONAL_PLANS[planId]
    const lookupKeys = Object.values(plan.stripeLookupKeys)
    if (lookupKeys.includes(normalizedLookupKey as PersonalStripeLookupKey)) {
      return planId
    }
  }

  if (normalizedLookupKey.startsWith('premium')) return 'ultra'
  if (normalizedLookupKey.startsWith('studio')) return 'ultra'
  if (normalizedLookupKey.startsWith('byok')) return 'pro'
  if (/^ultra_?(3x|5x|10x)(_|$)/.test(normalizedLookupKey)) return 'ultra'

  return null
}

export const getPersonalPlanLookupKey = (
  planId: LosiPersonalPlanId,
  billingCycle: BillingCycle = 'monthly'
) => {
  return LOSI_PERSONAL_PLANS[planId].stripeLookupKeys[billingCycle] ?? null
}

export const normalizeNexusInternalPlan = (
  plan: string | null | undefined
): NexusInternalPlanId | null => {
  if (!plan) return null

  const normalizedPlan = plan.toLowerCase()

  if (
    normalizedPlan === 'starter' ||
    normalizedPlan.startsWith('nexus_starter')
  ) {
    return 'starter'
  }

  if (
    normalizedPlan === 'professional' ||
    normalizedPlan.startsWith('nexus_professional')
  ) {
    return 'professional'
  }

  if (
    normalizedPlan === 'enterprise' ||
    normalizedPlan.startsWith('nexus_enterprise')
  ) {
    return 'enterprise'
  }

  return null
}

export const getBusinessPlanIdFromInternalNexusPlan = (
  plan: string | null | undefined
): LosiBusinessPlanId | null => {
  const normalizedPlan = normalizeNexusInternalPlan(plan)

  switch (normalizedPlan) {
    case 'starter':
      return 'business_starter'
    case 'professional':
      return 'business_pro'
    case 'enterprise':
      return 'business_ultra'
    default:
      return null
  }
}

export const getBusinessPlanFromLookupKey = (
  lookupKey: string | null | undefined
): LosiBusinessPlanId | null => {
  if (!lookupKey) return null

  const normalizedLookupKey = lookupKey.toLowerCase()

  for (const planId of BUSINESS_PLAN_ORDER) {
    const plan = LOSI_BUSINESS_PLANS[planId]
    const lookupKeys = Object.values(plan.stripeLookupKeys)
    if (lookupKeys.includes(normalizedLookupKey as BusinessStripeLookupKey)) {
      return planId
    }
  }

  return null
}

export const getBusinessPlanLookupKey = (
  planId: LosiBusinessPlanId,
  billingCycle: BillingCycle = 'monthly'
) => {
  return LOSI_BUSINESS_PLANS[planId].stripeLookupKeys[billingCycle] ?? null
}

export const getBusinessPlanByInternalNexusPlan = (
  plan: string | null | undefined
) => {
  const businessPlanId = getBusinessPlanIdFromInternalNexusPlan(plan)
  return businessPlanId ? LOSI_BUSINESS_PLANS[businessPlanId] : null
}

export const getNexusOverageRateUsd = (
  plan: NexusInternalPlanId | null | undefined
) => {
  const businessPlan = getBusinessPlanByInternalNexusPlan(plan)
  return businessPlan?.overageRateUsd ?? null
}

export const formatUsd = (amount: number) => `$${amount.toFixed(2)}`

export const formatUsdCents = (amountCents: number) =>
  formatUsd(amountCents / 100)

type BillingPlanConfig = {
  monthlyPriceUsd: number
  yearlyPriceUsd?: number
  yearlyMonthlyPriceUsd?: number
  yearlySavingsPercent?: number
}

export type FeaturePlanProfileLike = {
  plan?: string | null
  nexus_plan?: string | null
  nexus_plan_status?: string | null
}

const BUSINESS_ENTITLED_STATUSES = ['active', 'trialing'] as const

const getBillingPriceSnapshot = (
  plan: BillingPlanConfig,
  billingCycle: BillingCycle = 'monthly'
): LosiBillingPriceSnapshot => {
  if (
    billingCycle === 'yearly' &&
    typeof plan.yearlyPriceUsd === 'number' &&
    typeof plan.yearlyMonthlyPriceUsd === 'number'
  ) {
    return {
      billingCycle,
      monthlyEquivalentUsd: plan.yearlyMonthlyPriceUsd,
      billedUpfrontUsd: plan.yearlyPriceUsd,
      savingsPercent: plan.yearlySavingsPercent ?? 0
    }
  }

  return {
    billingCycle: 'monthly',
    monthlyEquivalentUsd: plan.monthlyPriceUsd,
    billedUpfrontUsd: plan.monthlyPriceUsd,
    savingsPercent: 0
  }
}

export const getPersonalPlanBillingPrice = (
  planId: LosiPersonalPlanId,
  billingCycle: BillingCycle = 'monthly'
) => getBillingPriceSnapshot(LOSI_PERSONAL_PLANS[planId], billingCycle)

export const getBusinessPlanBillingPrice = (
  planId: LosiBusinessPlanId,
  billingCycle: BillingCycle = 'monthly'
) => getBillingPriceSnapshot(LOSI_BUSINESS_PLANS[planId], billingCycle)

export const getAttachmentLimitConfig = (planId: LosiPublicPlanId) =>
  LOSI_ATTACHMENT_LIMITS[planId]

export const getExecutionLimitConfig = (planId: LosiPublicPlanId) =>
  LOSI_EXECUTION_LIMITS[planId]

export const getSpaceObjectAvailability = (
  planId: LosiPublicPlanId,
  objectType: LosiSpaceObjectType
) => LOSI_SPACE_OBJECT_GATES[planId][objectType]

export const canCreateSpaceObject = (
  planId: LosiPublicPlanId,
  objectType: LosiSpaceObjectType
) => getSpaceObjectAvailability(planId, objectType) !== 'no'

export const isSpaceObjectSupportPending = (
  planId: LosiPublicPlanId,
  objectType: LosiSpaceObjectType
) => getSpaceObjectAvailability(planId, objectType) === 'create_when_supported'

export const isBusinessPlanId = (
  planId: LosiPublicPlanId
): planId is LosiBusinessPlanId => {
  return String(planId).startsWith('business_')
}

export const getEffectiveFeaturePlanId = (
  profile: FeaturePlanProfileLike | null | undefined
): LosiPublicPlanId => {
  if (!profile) {
    return 'free'
  }

  if (
    BUSINESS_ENTITLED_STATUSES.includes(
      (profile?.nexus_plan_status ?? '') as (typeof BUSINESS_ENTITLED_STATUSES)[number]
    )
  ) {
    const businessPlanId = getBusinessPlanIdFromInternalNexusPlan(
      profile.nexus_plan
    )

    if (businessPlanId) {
      return businessPlanId
    }
  }

  if (isCanadaDayUltraWeekActive()) {
    return 'ultra'
  }

  return normalizePersonalPlanTier(profile?.plan)
}

export const hasStudioEntitlement = (
  profile: FeaturePlanProfileLike | null | undefined
) => {
  if (!profile) return false

  return (
    isLegacyStudioPlan(profile.plan) ||
    isBusinessPlanId(getEffectiveFeaturePlanId(profile))
  )
}

export const shouldRouteOverageToWallet = (planId: LosiPublicPlanId) =>
  isBusinessPlanId(planId)
